import dayjs from 'dayjs';
import type { LineSeries } from '../../../models/time-series';

const HOUR_MS = 60 * 60 * 1000;

/**
 * The window the series were asked for, as epoch milliseconds. Charts take their axis domain from
 * this rather than from the data they received, so a sensor that went quiet leaves a hole of the
 * right width instead of shortening the axis.
 */
export interface TimeRange {
  from: number;
  to: number;
}

export interface MergedRow {
  time: number;
  [seriesName: string]: number | null;
}

export interface DailyRow {
  day: string;
  [key: string]: string | number | null;
}

export const round = (value: number, precision: number): number => {
  const factor = 10 ** precision;

  return Math.round(value * factor) / factor;
};

/**
 * Floors to the hour on the UTC clock, matching how the API buckets readings. `dayjs.startOf`
 * would floor on the viewer's clock and miss the bucket by half an hour wherever the offset is
 * not a whole number of hours.
 */
const floorToHour = (value: string | number): number => Math.floor(new Date(value).getTime() / HOUR_MS) * HOUR_MS;

export const hasValues = (series: LineSeries[]): boolean => series.some((s) => s.data.some((p) => p.value != null));

/**
 * One row per hour of `range`, whether or not anything reported in it. An hour with no row at all
 * is drawn as if the neighbouring hours were adjacent, so the line closes over the gap and reads
 * as continuous measurement; an hour with a row and no value breaks the line, which is the truth.
 */
export function mergeSeries(series: LineSeries[], range: TimeRange): MergedRow[] {
  const rows = new Map<number, MergedRow>();

  for (let time = floorToHour(range.from); time <= range.to; time += HOUR_MS) {
    rows.set(time, { time });
  }

  for (const s of series) {
    for (const point of s.data) {
      if (point.value == null) {
        continue;
      }

      const row = rows.get(floorToHour(point.time));

      if (row) {
        row[s.name] = point.value;
      }
    }
  }

  return Array.from(rows.values());
}

/**
 * One row per day of `range`, carrying each series' daily mean, extremes and spread. The extremes
 * come from the hourly `min`/`max` rather than from the hourly means, so the day's real high and
 * low survive the downsample the API performs.
 */
export function dailyRows(series: LineSeries[], range: TimeRange, precision = 1): DailyRow[] {
  interface DayBucket {
    sum: number;
    count: number;
    min: number;
    max: number;
  }

  const days: string[] = [];

  for (let day = dayjs(range.from).startOf('day'); day.valueOf() <= range.to; day = day.add(1, 'day')) {
    days.push(day.format('YYYY-MM-DD'));
  }

  const buckets = new Map<string, Map<string, DayBucket>>(days.map((day) => [day, new Map<string, DayBucket>()]));

  for (const s of series) {
    for (const point of s.data) {
      if (point.value == null) {
        continue;
      }

      const day = buckets.get(dayjs(point.time).format('YYYY-MM-DD'));

      if (!day) {
        continue;
      }

      const low = point.min ?? point.value;
      const high = point.max ?? point.value;
      const bucket = day.get(s.name);

      if (bucket) {
        bucket.sum += point.value;
        bucket.count += 1;
        bucket.min = Math.min(bucket.min, low);
        bucket.max = Math.max(bucket.max, high);
      } else {
        day.set(s.name, { sum: point.value, count: 1, min: low, max: high });
      }
    }
  }

  return days.map((day) => {
    const row: DailyRow = { day: dayjs(day).format('MM/DD') };

    for (const s of series) {
      const bucket = buckets.get(day)?.get(s.name);

      row[s.name] = bucket ? round(bucket.sum / bucket.count, precision) : null;
      row[`${s.name}Min`] = bucket ? round(bucket.min, precision) : null;
      row[`${s.name}Max`] = bucket ? round(bucket.max, precision) : null;
      row[`${s.name}Spread`] = bucket ? round(bucket.max - bucket.min, precision) : null;
    }

    return row;
  });
}

/** Every fourth hour, anchored on local midnight so the bold day labels land on day boundaries. */
function hourTicks(range: TimeRange, everyHours = 4): number[] {
  const ticks: number[] = [];

  for (let tick = dayjs(range.from).startOf('day'); tick.valueOf() <= range.to; tick = tick.add(everyHours, 'hour')) {
    if (tick.valueOf() >= range.from) {
      ticks.push(tick.valueOf());
    }
  }

  return ticks;
}

function TimeTick({ x, y, payload }: any) {
  const at = dayjs(payload.value);
  const isMidnight = at.hour() === 0;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={10}
        textAnchor="end"
        transform="rotate(-45)"
        fill={isMidnight ? '#111827' : '#9ca3af'}
        fontSize={10}
        fontWeight={isMidnight ? 600 : 400}
      >
        {isMidnight ? at.format('D MMM') : at.format('HH:mm')}
      </text>
    </g>
  );
}

/**
 * Spread onto a recharts `XAxis`. A numeric time scale rather than the default category scale:
 * a category axis spaces the rows it was given evenly, so an outage renders as a normal hour
 * step instead of the gap it is.
 */
export const timeAxisProps = (range: TimeRange) => ({
  dataKey: 'time',
  type: 'number' as const,
  scale: 'time' as const,
  domain: [range.from, range.to],
  ticks: hourTicks(range),
  tick: <TimeTick />,
  interval: 0 as const,
  height: 60
});

/** Spread onto a recharts `XAxis` on the daily bar charts. */
export const dayAxisProps = {
  dataKey: 'day',
  tick: { fontSize: 11, fill: '#9ca3af' },
  axisLine: false,
  tickLine: false
};

export const formatTimeLabel = (label: number | string): string => dayjs(label).format('ddd, D MMM HH:mm');
